import React, { useEffect, useState } from "react";

import { MdmObjectDto, useGetObjectsPagedLazyQuery, useGetObjectsPagedQuery } from "gql/generated/types";
import { DataTablePFSEvent } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Link } from "react-router-dom";
import { TableColumn } from "utils/types";
import LazyTable from "../components/LazyTable";
import MapWrapper from "../components/MapWrapper";
import ObjectMapContent from "../components/ObjectMapContent";
import useProjectContext from "../hooks/useProjectContext";
import { LazyState } from "../utils/types";
import { errorToaster } from "../utils/utils";
import { OBJECTS_TABLE_NAME } from "../utils/const";

const Objects = () => {
  const {projectId, userSettings} = useProjectContext();
  const userTableDetails = () => userSettings?.userSettings.tablesDetails?.find(r => r?.table === OBJECTS_TABLE_NAME);

  const [lazyState, setLazyState] = useState<LazyState>({
    first: 0,
    rows: userTableDetails()?.rows || 10,
    page: 1,
    sortField: "identifier",
    sortOrder: 1,
    multiSortMeta: [],
    filters: {},
    andFilters: [],
    internalFilters: [],
  });
  const [getNextObjects,{data: objectData, loading }] = useGetObjectsPagedLazyQuery({
    onError: err => errorToaster(err)
  })

  useEffect(() => {
    if(!objectData && !loading) {
      getNextObjects({
        variables: {
          input: {
            projectId: projectId!,
            take: userTableDetails()?.rows || 10,
            skip: 0
          }
        },
      })
    }
  }, [objectData]);

  const objectInfoBtn = (rowData: MdmObjectDto) => (
    <Link to={`objects/${rowData.id}`} title="Object Info">
      {rowData.identifier}
    </Link>
  );

  const objectNameTemplate = (rowData: MdmObjectDto) => (
    <div className="truncate-text text-right lg:text-left" title={rowData.title ?? ""}>
      {rowData.title}
    </div>
  );

  const columns: TableColumn<MdmObjectDto>[] = [
    { field: "identifier", header: "Identifier", body: objectInfoBtn, sortable: true },
    { field: "title", header: "Object Name", body: objectNameTemplate, sortable: true },
    {
      field: "address.fullAddress",
      header: "Address",
      sortable: true,
    },
  ];

  const tableData = (objectData?.mdmObjectsByProjectIdPaged.items || []) as MdmObjectDto[];

  return (
    <TabView panelContainerClassName="p-0 sm:p-4">
      <TabPanel header="Table">
        <LazyTable
          columns={columns as TableColumn<Partial<MdmObjectDto>>[]}
          data={tableData}
          tableName={OBJECTS_TABLE_NAME}
          loading={loading}
          onLazyEvent={setLazyState}
          lazyState={lazyState}
          totalItems={objectData?.mdmObjectsByProjectIdPaged.pageInfo?.total || 0}
          getNextQuery={getNextObjects}
        />
      </TabPanel>
      <TabPanel header="Map">
        <ObjectProvider lazyState={lazyState} records={objectData?.mdmObjectsByProjectIdPaged.pageInfo?.total || 100}/>
      </TabPanel>
    </TabView>
  );
};

export default Objects;

type DeviceProviderProps = {
  lazyState: DataTablePFSEvent;
  records: number;
};
const ObjectProvider: React.FC<DeviceProviderProps> = ({lazyState,records}) => {
  const {projectId} = useProjectContext();

  const { data, loading } = useGetObjectsPagedQuery({
    variables: {
      input: {
        projectId,
        skip: 0,
        take: records,
        andFilters: lazyState.andFilters,
      },
    },
  });

  return (
    <MapWrapper>
      {loading && <ProgressSpinner className='mt-8 w-full' style={{zIndex: 400}}/>}
      <ObjectMapContent objects={(data?.mdmObjectsByProjectIdPaged.items ?? []) as MdmObjectDto[]} />
    </MapWrapper>
  );
}